<template>
  <v-tabs v-model="tab" :class="{ 'tabs-hidden': tabs.length <= 1 }">
    <v-tab v-for="tab in tabs" :key="tab.value" :tab-value="tab.value">{{ tab.text }}</v-tab>
    <v-tabs-items v-model="tab">
      <v-tab-item v-for="tab in tabs" :key="tab.value" :value="tab.value">
        <v-row no-gutters class="py-4 px-4 align-center" style="gap: 0.5rem">
          <v-col cols="auto">
            <base-rounded-button
              icon="mdi-plus"
              size="x-small"
              color="success"
              @click="openAddDialog"
              :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.addNew_${tab.value}`)"
            />
          </v-col>
          <v-col v-if="currentMetadata.type === 'Monitoring'" cols="auto">
            <base-rounded-button
              icon="mdi-upload"
              size="x-small"
              color="primary"
              @click="openUploadMappingDialog"
              :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.importGeom`)"
            />
          </v-col>
          <v-col cols="auto" style="flex-grow: 1" class="ml-2">
            <data-input
              ref="searchInput"
              v-model="searchTexts[tab.value]"
              @input="filterLists(tab.value)"
              @change="filterLists(tab.value)"
              v-bind="searchTypeInput"
            />
          </v-col>
        </v-row>
        <v-row v-if="(filteredItems[tab.value] || []).length" no-gutters>
          <v-col>
            <v-list>
              <v-list-item-group v-model="computedSelectedSite">
                <template v-for="(item, index) in filteredItems[tab.value] || []">
                  <v-list-item :key="`${tab.value}-${item.id}`" :value="item.id">
                    <v-list-item-content>
                      <v-row no-gutters class="fill-height" style="min-height: 100px;">
                        <v-col v-if="sitesMetadata.attributes.image" cols="4" style="max-height: 140px;">
                          <img
                            :src="getImageSource(item.image)"
                            @error="handleImageError"
                            style="width: 100%; height: 100%; object-fit: cover;"
                          />
                        </v-col>
                        <v-col class="pa-3">
                          <div style="height: 100%;" class="d-flex flex-column align-start justify-space-between">
                            <div style="text-align: left; gap: 0.2rem" class="d-flex flex-column">
                              <div class="font-weight-medium">{{ item.title }}</div>
                              <div v-if="item.subtitle" class="text-caption text--secondary font-italic">
                                {{ item.subtitle }}
                              </div>
                            </div>
                            <div v-if="item.code" class="text-body-2 primary--text">
                              {{
                                currentMetadata.type === 'Monitoring' ? item.code : `${codeAttributeName}: ${item.code}`
                              }}
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="index !== items.length - 1" :key="`divider-${item.id}`"></v-divider>
                </template>
              </v-list-item-group>
            </v-list>
          </v-col>
        </v-row>
        <v-row v-else no-gutters class="mt-4 mb-8">
          <v-col class="text--disabled text-subtitle-1">
            {{ $i18n.t('default.noData') }}
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    <add-edit-feature-dialog
      v-if="isAddEditFeatureLoaded"
      :key="addEditDialogKey"
      :title="$i18n.t(`table.rzi${currentMetadata.type}.addNew_${tab}`)"
      :is-visible.sync="isAddEditFeatureVisible"
      :custom-datasource-schema="protectedSitesDatasourceSchemaUpdated"
      v-bind="addingData()"
      @done="closeModal => $emit('siteAdded', closeModal)"
    />
    <rzi-upload-geometry-dialog
      v-if="isUploadDialogLoaded"
      :current-metadata="currentMetadata"
      :is-visible.sync="isUploadDialogVisible"
      @done="handleMassImport"
    />
  </v-tabs>
</template>

<script>
import { debounce } from 'lodash';
import { get, sync } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { utils } from '@/mixins/rziHelpers';

import { sitesTypes } from '@/assets/js/rziData';

import DataInput from '@/components/DataInput';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import RziUploadGeometryDialog from '@/components/RziUploadGeometryDialog';

export default {
  name: 'RziSideLists',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    DataInput,
    AddEditFeatureDialog,
    RziUploadGeometryDialog,
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    items: {
      type: Array,
      default: () => [],
    },
    currentTab: {
      type: String,
    },
    selectedSite: {
      type: Number,
      default: null,
    },
    tabInfo: {
      type: Object,
    },
  },
  data: () => ({
    isUploadDialogLoaded: false,
    isUploadDialogVisible: false,
    isAddEditFeatureLoaded: false,
    isAddEditFeatureVisible: false,
    addEditDialogKey: '',
    searchTexts: {},
    protectedSitesDatasourceSchemaUpdated: [],
    filteredItems: {},
  }),
  computed: {
    dataSources: get('layers/metadata'),
    sitesTypesFeatures: sync('rziData/sitesTypesFeatures'),
    tab: {
      get() {
        return this.currentTab;
      },
      set(nV) {
        this.$emit('update:currentTab', nV);
      },
    },
    sitesMetadata() {
      return this.currentMetadata.protectedSites;
    },
    protectedSitesDatasourceSchema() {
      return this.dataSources[this.sitesMetadata.datasource]?.attributes_schema;
    },
    codeAttributeName() {
      return this.protectedSitesDatasourceSchema.attributes.find(
        attr => attr.name === this.sitesMetadata.attributes.code
      )?.verbose_name;
    },
    computedSelectedSite: {
      get() {
        return this.selectedSite;
      },
      set(nV) {
        this.$emit('update:selectedSite', nV);
      },
    },
    tabs() {
      return this.currentMetadata.protectedSites.categories.map(tab => ({
        ...tab,
        ...(sitesTypes.categoriesData[tab.value] ?? {}),
        text: this.$i18n.t(`table.rzi${this.currentMetadata.type}.${tab.value}`),
      }));
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t(`table.rzi${this.currentMetadata.type}.searchLabel`),
        hideDetails: true,
        clearable: true,
      };
    },
  },
  watch: {
    items: {
      deep: true,
      immediate: true,
      handler(nV) {
        this.parsedItems = this.prepareProtectedSiteFeatures([...nV]);
        this.filteredItems = JSON.parse(JSON.stringify(this.parsedItems));
        this.filterLists(this.currentTab);
      },
    },
  },
  methods: {
    handleMassImport(closeDialog) {
      this.$emit('refreshYearGeometry', closeDialog);
    },
    openUploadMappingDialog() {
      this.isUploadDialogLoaded = this.isUploadDialogVisible = true;
    },
    addingData() {
      return {
        featureData: {
          datasource: this.sitesMetadata.datasource,
        },
        ...(this.tabInfo?.documentFeature?.id && {
          staticProperties: {
            [this.sitesMetadata.attributes.docsId]: this.tabInfo.documentFeature.id,
          },
        }),
        hiddenAttributes: ['_systems', ...this.sitesMetadata.hiddenAttributes],
      };
    },
    openAddDialog() {
      switch (this.currentMetadata.type) {
        case 'Monitoring': {
          this.addEditDialogKey = `adddialog-${this.currentTab}-${this.filteredItems[this.currentTab]?.length}`;
          this.protectedSitesDatasourceSchemaUpdated = null;
          this.isAddEditFeatureLoaded = this.isAddEditFeatureVisible = true;
          break;
        }
        case 'Documents': {
          const tabTypesIds = this.sitesTypesFeatures
            .filter(siteFt => siteFt.properties.custom_category === this.currentTab)
            .map(ft => ft.id);
          const codesToExclude = this.filteredItems[this.currentTab]?.map(item => item.code);
          let newDatasourceAttributes = JSON.parse(JSON.stringify(this.protectedSitesDatasourceSchema.attributes));
          let foundAttribute = newDatasourceAttributes.find(attr => attr.name === this.sitesMetadata.attributes.title);
          if (!foundAttribute) return;
          const sitesDictMetadata = this.currentMetadata.protectedSitesDict;
          foundAttribute.relation.filter_expression = JSON.stringify({
            '!AND': [
              {
                $IN: {
                  [`${sitesDictMetadata.datasource}.${sitesDictMetadata.attributes.type}`]: tabTypesIds,
                },
              },
              ...(codesToExclude?.length
                ? [
                    {
                      NOT: {
                        $IN: {
                          [`${sitesDictMetadata.datasource}.${sitesDictMetadata.attributes.code}`]: codesToExclude,
                        },
                      },
                    },
                  ]
                : []),
            ],
          });
          this.addEditDialogKey = `adddialog-${this.currentTab}-${this.filteredItems[this.currentTab]?.length}`;
          this.protectedSitesDatasourceSchemaUpdated = newDatasourceAttributes;
          this.isAddEditFeatureLoaded = this.isAddEditFeatureVisible = true;
          break;
        }
      }
    },
    prepareProtectedSiteFeatures(features) {
      return features.reduce((acc, item) => {
        const typeValue = item.properties[this.sitesMetadata.attributes.type];
        const appliedTabName = this.tabs.find(tab =>
          tab.allowedTypeValues ? tab.allowedTypeValues.includes(typeValue) : true
        )?.value;
        if (!appliedTabName) return acc;
        const featureObject = {
          id: item.id,
          ...['title', 'subtitle', 'code', 'image'].reduce((acc2, key) => {
            if (this.currentMetadata.type === 'Monitoring' && key === 'code') {
              acc2[key] = item.id + '';
              return acc2;
            }
            if (!this.sitesMetadata.attributes[key]) return acc2;
            acc2[key] = this.getAttributeTextValueByName(
              item.properties[this.sitesMetadata.attributes[key]],
              this.sitesMetadata.attributes[key],
              this.sitesMetadata.datasource
            );
            return acc2;
          }, {}),
        };
        if (!acc[appliedTabName]) acc[appliedTabName] = [];
        acc[appliedTabName].push(featureObject);
        return acc;
      }, {});
    },
    handleImageError(er) {
      er.target.src = this.getImagePlaceholder();
    },
    filterLists(tab) {
      const text = this.searchTexts[tab];
      let itemsCopy = JSON.parse(JSON.stringify(this.parsedItems));
      this.filteredItems = {
        ...this.filteredItems,
        [tab]: text
          ? (itemsCopy[tab] || []).filter(item =>
              (this.currentMetadata.type === 'Monitoring' ? ['title', 'subtitle', 'code'] : ['title', 'code'])
                .filter(i => item[i])
                .map(i => item[i].toLowerCase())
                .some(item2 => item2.includes((text ?? '').toLowerCase()))
            )
          : itemsCopy[tab] || [],
      };
    },
  },
  created() {
    this.filterLists = debounce(this.filterLists, 250);
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-tab) {
  flex-grow: 1;
}
.tabs-hidden :deep(.v-tabs-bar) {
  display: none !important;
}
:deep(.v-list),
:deep(.v-list-item__content),
:deep(.v-list-item) {
  padding: 0 !important;
}
</style>
